import { GraphQLString, GraphQLFloat, GraphQLBoolean, GraphQLObjectType, GraphQLList, GraphQLSchema, GraphQLInputObjectType, GraphQLInt, } from 'graphql';
// import { Properties, buildHelpers } from '../create-public-schema';
import { repositoryForSchema } from '../repository-for-schema';
import { getGraphQLQueryArgs, getMongoDbQueryResolver, getMongoDbFilter } from 'graphql-to-mongodb';
import { GraphQLDateTime } from 'graphql-iso-date';
import chalk from 'chalk';
import { MongoIdType } from './mongo-id-type';
import '../plugins/single-ref-plugin';
import '../plugins/multiple-ref-plugin';
import 'immer';
import { EventService } from '../events/event-service';
export class SchemaBuilder {
    constructor() {
        this.types = [];
        this.inputTypes = [];
    }
    init(serverConfig) {
        this.types = [];
        this.inputTypes = [];
        this.serverConfig = serverConfig;
        this.eventService = new EventService(serverConfig);
        // this.serverConfig = produce(serverConfig, (newServerOptions) => {
        //   newServerOptions.resolverPlugins = newServerOptions.resolverPlugins || [];
        //   newServerOptions.resolverPlugins.push(singleRefPlugin);
        //   newServerOptions.resolverPlugins.push(multipleRefPlugin);
        // });
    }
    getTypeFromSchema(entitySchema) {
        return this.buildGraphQLObjectFromSchema({
            entitySchema,
        });
    }
    getEntityTypeFromSchema(entitySchema) {
        return this.buildGraphQLObjectFromSchema({
            entitySchema,
            prefixName: '',
            addResolvers: false,
            suffixName: 'Entity',
        });
    }
    buildSchema(schema) {
        let publicQueryFields = {};
        schema.forEach((entitySchema) => {
            const type = this.buildGraphQLObjectFromSchema({
                entitySchema,
                prefixName: '',
                addResolvers: true,
            });
            const repository = repositoryForSchema(entitySchema);
            publicQueryFields = {
                ...publicQueryFields,
                ...this.buildPublicFieldQueries(entitySchema, repository, type),
            };
            console.log(chalk.blue(`Added schema: ${entitySchema.options.displayName || entitySchema.options.alias}`));
        });
        const publicQuery = new GraphQLObjectType({
            name: 'Query',
            fields: publicQueryFields,
        });
        const publicGraphQLSchema = new GraphQLSchema({ query: publicQuery });
        let internalQueryFields = {};
        schema.forEach((entitySchema) => {
            const repository = repositoryForSchema(entitySchema);
            internalQueryFields = {
                ...internalQueryFields,
                ...this.buildInternalFieldQueries(entitySchema, repository),
            };
        });
        const internalQuery = new GraphQLObjectType({
            name: 'Query',
            fields: internalQueryFields,
        });
        let mutationFields = {};
        schema.forEach((entitySchema) => {
            const repository = repositoryForSchema(entitySchema);
            mutationFields = {
                ...mutationFields,
                ...this.buildFieldMutations(entitySchema, repository),
            };
        });
        const mutation = new GraphQLObjectType({
            name: 'Mutation',
            fields: mutationFields,
        });
        const internalGraphQLSchema = new GraphQLSchema({ query: internalQuery, mutation });
        this.eventService.onSchemaBuilt(publicGraphQLSchema);
        return {
            publicGraphQLSchema,
            internalGraphQLSchema,
        };
    }
    buildInternalFieldQueries(entitySchema, repository) {
        const entityType = this.getEntityTypeFromSchema(entitySchema);
        const args = getGraphQLQueryArgs(entityType);
        const resolvers = {
            [`${entitySchema.options.alias}EntityList`]: {
                type: new GraphQLList(entityType),
                args,
                resolve: getMongoDbQueryResolver(entityType, async (filter, projection, options, obj, args, { db }) => {
                    return repository
                        .find(filter)
                        .sort(options.sort)
                        .limit(options.limit || 100)
                        .skip(options.skip || 0);
                }),
            },
            [`${entitySchema.options.alias}Count`]: {
                type: GraphQLInt,
                args: {
                    filter: args.filter,
                },
                resolve: (_, { filter }) => repository.countDocuments(getMongoDbFilter(entityType, filter)),
            },
            [`${entitySchema.options.alias}EntityFindById`]: {
                type: entityType,
                args: {
                    id: { type: GraphQLString },
                },
                resolve: (_, { id }) => {
                    return repository.findById(id);
                },
            },
        };
        return resolvers;
    }
    buildPublicFieldQueries(entitySchema, repository, type) {
        const entityType = this.buildGraphQLObjectFromSchema({
            entitySchema,
            prefixName: '',
            addResolvers: false,
            suffixName: 'Entity',
        });
        const inputType = this.buildInput(`${entitySchema.options.alias}Input`, entitySchema.properties);
        const args = getGraphQLQueryArgs(entityType);
        const resolvers = {
            [`${entitySchema.options.alias}Count`]: {
                type: GraphQLInt,
                args: {
                    filter: args.filter,
                },
                resolve: (_, { filter }) => repository.count(getMongoDbFilter(entityType, filter)),
            },
            [`${entitySchema.options.alias}List`]: {
                type: new GraphQLList(type),
                args,
                resolve: getMongoDbQueryResolver(entityType, async (filter, projection, options, obj, args, { db }) => {
                    return repository
                        .find(filter)
                        .sort(options.sort)
                        .limit(options.limit || 100)
                        .skip(options.skip || 0);
                }, {
                    differentOutputType: true,
                }),
            },
            [`${entitySchema.options.alias}Preview`]: {
                type,
                args: {
                    record: { type: inputType },
                },
                resolve: (_, { record }, { userId }) => {
                    return record;
                },
            },
        };
        if (entitySchema.options.maxOne) {
            return {
                ...resolvers,
                [`${entitySchema.options.alias}`]: {
                    type,
                    args: {},
                    resolve: async (obj, {}, context) => {
                        return repository.findOne();
                    },
                },
            };
        }
        else {
            return {
                ...resolvers,
                [`${entitySchema.options.alias}FindById`]: {
                    type,
                    args: {
                        id: { type: GraphQLString },
                    },
                    resolve: (_, { id }) => {
                        return repository.findById(id);
                    },
                },
                [`${entitySchema.options.alias}FindOne`]: {
                    type,
                    args,
                    resolve: getMongoDbQueryResolver(entityType, async (filter, projection, options, obj, args, { db }) => {
                        return repository
                            .findOne(filter)
                            .sort(options.sort)
                            .limit(options.limit || 100)
                            .skip(options.skip || 0);
                    }, {
                        differentOutputType: true,
                    }),
                },
            };
        }
    }
    buildFieldMutations(entitySchema, repository) {
        const inputType = this.buildInput(`${entitySchema.options.alias}Input`, entitySchema.properties);
        const entityType = this.getEntityTypeFromSchema(entitySchema);
        return {
            [`${entitySchema.options.alias}Create`]: {
                type: entityType,
                args: {
                    record: { type: inputType },
                },
                resolve: (_, { record }, { userId }) => {
                    if (!userId) {
                        throw new Error('AuthenticationError');
                    }
                    return repository.create(record);
                },
            },
            [`${entitySchema.options.alias}Update`]: {
                type: entityType,
                args: {
                    record: { type: inputType },
                },
                resolve: async (_, { record }, { userId }) => {
                    if (!userId) {
                        throw new Error('AuthenticationError');
                    }
                    if (!record._id) {
                        throw new Error('Missing _id');
                    }
                    await repository.findByIdAndUpdate(record._id, record);
                    return repository.findById(record._id);
                },
            },
            [`${entitySchema.options.alias}RemoveById`]: {
                type: GraphQLBoolean,
                args: {
                    id: { type: GraphQLString },
                },
                resolve: async (_, { id }, { userId }) => {
                    if (!userId) {
                        throw new Error('AuthenticationError');
                    }
                    await repository.findByIdAndDelete(id);
                    return true;
                },
            },
        };
    }
    buildType(propertyName, propertyType) {
        switch (true) {
            case propertyType === String: {
                return GraphQLString;
            }
            case propertyType === Date: {
                return GraphQLDateTime;
            }
            case propertyType === Number: {
                return GraphQLFloat;
            }
            case propertyType === Boolean: {
                return GraphQLBoolean;
            }
            case propertyType instanceof Array: {
                const type = this.buildType(propertyName, propertyType[0]);
                return new GraphQLList(type);
            }
            case propertyType instanceof Object: {
                return this.buildShape(propertyName, propertyType);
            }
            // @ts-ignore
            // case 'SchemaType': {
            //   // @ts-ignore
            //   return this.buildGraphQLObjectFromSchema(propertyType.meta, '');
            // }
            // case 'Ref': {
            //   const shapeArgs = Object.keys(propertyType.meta.properties).reduce((acc, propertKey) => {
            //     acc[propertKey] = propertyType.meta.properties[propertKey].type;
            //     return acc;
            //   }, {}) as any;
            //   const shape = RefractTypes.shape(shapeArgs);
            //   return this.buildShape(propertyName, shape);
            // }
            default: {
                return GraphQLString;
            }
        }
    }
    buildInputType(propertyName, propertyType) {
        switch (true) {
            case propertyType === String: {
                return GraphQLString;
            }
            case propertyType === Date: {
                return GraphQLDateTime;
            }
            case propertyType === Number: {
                return GraphQLFloat;
            }
            case propertyType === Boolean: {
                return GraphQLBoolean;
            }
            case propertyType instanceof Array: {
                const type = this.buildInputType(propertyName, propertyType[0]);
                return new GraphQLList(type);
            }
            case propertyType instanceof Object: {
                return this.buildShapeInput(propertyName, propertyType);
            }
            default: {
                return GraphQLString;
            }
        }
    }
    buildInput(alias, properties) {
        const existingType = this.types.find((t) => t.name === alias);
        if (existingType) {
            return existingType;
        }
        const inputTypes = new GraphQLInputObjectType({
            name: alias,
            fields: () => Object.keys(properties).reduce((acc, propertyKey) => {
                const propertyType = properties[propertyKey].type;
                const type = this.buildInputType(`${alias}${propertyKey}`, propertyType);
                acc[propertyKey] = {
                    type,
                };
                return acc;
            }, {
                _id: {
                    type: MongoIdType,
                },
            }),
        });
        this.inputTypes.push(inputTypes);
        return inputTypes;
    }
    buildEntity(alias, properties, addResolvers) {
        const existingType = this.types.find((t) => t.name === alias);
        if (existingType) {
            return existingType;
        }
        const extraProperties = this.serverConfig.resolvers && addResolvers ? this.serverConfig.resolvers[alias] || {} : {};
        const editableAndResolvedProperties = {
            ...properties,
            ...extraProperties,
        };
        const type = new GraphQLObjectType({
            name: alias,
            fields: () => Object.keys(editableAndResolvedProperties).reduce((acc, propertyKey) => {
                const propertyOptions = editableAndResolvedProperties[propertyKey];
                const propertyType = propertyOptions.type;
                const type = this.buildType(`${alias}${propertyKey}`, propertyType);
                acc[propertyKey] = {
                    type,
                };
                if (addResolvers && propertyOptions.resolve) {
                    acc[propertyKey].resolve = propertyOptions.resolve;
                    acc[propertyKey].args = propertyOptions.args;
                    acc[propertyKey].dependencies = [];
                }
                else if (addResolvers &&
                    propertyOptions.resolverPlugin &&
                    this.serverConfig.resolverPlugins.some((plugin) => plugin.alias === propertyOptions.resolverPlugin.alias)) {
                    const plugin = this.serverConfig.resolverPlugins.find((plugin) => plugin.alias === propertyOptions.resolverPlugin.alias);
                    acc[propertyKey] = plugin.buildFieldConfig({
                        propertyKey,
                        meta: propertyOptions.resolverPlugin.meta,
                        serverConfig: this.serverConfig,
                        schemaBuilder: this,
                    });
                }
                return acc;
            }, {
                _id: {
                    type: MongoIdType,
                },
            }),
        });
        this.types.push(type);
        return type;
    }
    buildShape(propertyName, propertyType) {
        return new GraphQLObjectType({
            name: propertyName,
            fields: Object.keys(propertyType).reduce((acc, propertyKey) => {
                const type = this.buildType(`${propertyName}${propertyKey}`, propertyType[propertyKey]);
                acc[propertyKey] = {
                    type,
                };
                return acc;
            }, {}),
        });
    }
    buildShapeInput(propertyName, propertyType) {
        return new GraphQLInputObjectType({
            name: propertyName,
            fields: Object.keys(propertyType).reduce((acc, propertyKey) => {
                const type = this.buildInputType(`${propertyName}${propertyKey}`, propertyType[propertyKey]);
                acc[propertyKey] = {
                    type,
                };
                return acc;
            }, {}),
        });
    }
    buildGraphQLObjectFromSchema({ entitySchema, prefixName = '', addResolvers = true, suffixName = '', }) {
        const type = this.buildEntity(prefixName + entitySchema.options.alias + suffixName, entitySchema.properties, addResolvers);
        return type;
    }
}
export const schemaBuilder = new SchemaBuilder();
